import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRole, updateRole, listRoles } from '../../services/roles';
import Toggle from '../../components/ui/Toggle';
import toast from 'react-hot-toast';

const RoleDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const [role, setRole] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleName, setRoleName] = useState('');
  const [roleDescription, setRoleDescription] = useState('');
  const [permissions, setPermissions] = useState({});
  const [saving, setSaving] = useState(false);

  // Modules list based on sidebar navigation
  // This matches all modules shown in the sidebar
  const modules = [
    {
      name: 'Dashboard',
      permissionKey: 'dashboard',
      requiredPermission: 'dashboard:read'
    },
    {
      name: 'COA Library',
      permissionKey: 'coa',
      requiredPermission: 'coa:read'
    },
    {
      name: '3rd Party COAs',
      permissionKey: 'thirdpartycoa',
      requiredPermission: 'thirdpartycoa:read'
    },
    {
      name: 'Requested Licences',
      permissionKey: 'licence',
      requiredPermission: 'licence:read'
    },
    {
      name: 'My Review Queue',
      permissionKey: 'licence',
      requiredPermission: 'licence:review',
      specialPermission: 'licence:review' // Special handling - only Read toggle
    },
    {
      name: 'My Approval Queue',
      permissionKey: 'licence',
      requiredPermission: 'licence:approve',
      specialPermission: 'licence:approve' // Special handling - only Read toggle
    },
    {
      name: 'Invoices',
      permissionKey: 'transaction',
      requiredPermission: 'transaction:read'
    },
    {
      name: 'Quotations',
      permissionKey: 'quotation',
      requiredPermission: 'quotation:read'
    },
    {
      name: 'Customers',
      permissionKey: 'customer',
      requiredPermission: 'customer:read'
    },
    {
      name: 'Messages',
      permissionKey: 'message',
      requiredPermission: 'message:read'
    },
    {
      name: 'Uploads',
      permissionKey: 'upload',
      requiredPermission: 'upload:create',
      uploadOnlyCreate: true // Only Create toggle (upload support documents); Read/Edit/Delete not used
    },
    {
      name: 'Notifications',
      permissionKey: 'notification',
      requiredPermission: 'notification:read'
    },
    {
      name: 'Personal Details',
      permissionKey: 'profile',
      requiredPermission: 'profile:read'
    },
    {
      name: 'Admin Team',
      permissionKey: 'user',
      requiredPermission: 'user:read'
    },
    {
      name: 'Roles & Permissions',
      permissionKey: 'role',
      requiredPermission: 'role:read'
    },
    {
      name: 'Xero Integration',
      permissionKey: 'settings',
      requiredPermission: 'settings:read',
      specialPermission: '*' // SuperAdmin only
    },
    {
      name: 'CMS',
      permissionKey: 'cms',
      requiredPermission: 'cms:read'
    }
  ];

  // Permission types
  const permissionTypes = ['Create', 'Read', 'Edit', 'Delete'];

  useEffect(() => {
    if (id) {
      loadRole();
    }
  }, [id]);

  const loadRole = async () => {
    try {
      setLoading(true);
      // Try to get role by ID, fallback to listRoles if getRole doesn't exist
      let foundRole;
      try {
        const response = await getRole(id);
        foundRole = response.data;
      } catch (err) {
        // Fallback: use listRoles and find the role
        const response = await listRoles();
        foundRole = response.data?.find(r => (r._id || r.id) === id);
      }
      
      if (foundRole) {
        setRole(foundRole);
        setRoleName(foundRole.name || '');
        setRoleDescription(foundRole.description || '');
        
        // Initialize permissions from role data
        const perms = {};
        modules.forEach(module => {
          perms[module.name] = {
            Create: false,
            Read: false,
            Edit: false,
            Delete: false
          };
        });
        
        // If role has permissions array, map them
        if (foundRole.permissions && Array.isArray(foundRole.permissions)) {
          foundRole.permissions.forEach(perm => {
            // Handle SuperAdmin with '*' permission
            if (perm === '*') {
              // SuperAdmin has all permissions - enable all toggles
              modules.forEach(module => {
                if (module.specialPermission === '*') {
                  // Only enable Read for special permissions like Xero
                  perms[module.name]['Read'] = true;
                } else {
                  // Enable all CRUD for regular modules
                  permissionTypes.forEach(pt => {
                    perms[module.name][pt] = true;
                  });
                }
              });
              return;
            }
            
            // Parse permission string like "licence:create" or "dashboard:read" or "licence:review" or "coa:read"
            const parts = perm.split(':');
            if (parts.length === 2) {
              const [moduleKey, action] = parts;
              
              // Handle special cases for Review Queue and Approval Queue
              // Each queue has its own permission; licence:update applies only to Requested Licences
              if (action === 'review') {
                const module = modules.find(m => m.name === 'My Review Queue');
                if (module && perms[module.name]) {
                  perms[module.name]['Read'] = true;
                  perms[module.name]['Edit'] = true;
                }
              } else if (action === 'approve') {
                const module = modules.find(m => m.name === 'My Approval Queue');
                if (module && perms[module.name]) {
                  perms[module.name]['Read'] = true;
                  perms[module.name]['Edit'] = true;
                }
              } else if (action === 'update' && moduleKey === 'licence') {
                // licence:update = Edit for Requested Licences only (not Review Queue or Approval Queue)
                const requestedModule = modules.find(m => m.name === 'Requested Licences');
                if (requestedModule && perms[requestedModule.name]) {
                  perms[requestedModule.name]['Edit'] = true;
                }
              } else {
                // Standard CRUD permissions - find all modules by permissionKey
                const matchedModules = modules.filter(m => m.permissionKey === moduleKey.toLowerCase());
                matchedModules.forEach(module => {
                  if (module && perms[module.name]) {
                    // Map "update" back to "Edit" for display
                    const displayAction = action === 'update' ? 'edit' : action;
                    const actionName = capitalizeFirst(displayAction);
                    if (permissionTypes.includes(actionName)) {
                      perms[module.name][actionName] = true;
                    }
                  }
                });
              }
            }
          });
        }
        
        setPermissions(perms);
      }
    } catch (err) {
      console.error('Failed to load role:', err);
    } finally {
      setLoading(false);
    }
  };

  // Helper function to get module by permission key
  const getModuleByPermissionKey = (key) => {
    return modules.find(m => m.permissionKey === key.toLowerCase());
  };

  const capitalizeFirst = (str) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  const handleTogglePermission = (module, permissionType) => {
    setPermissions(prev => ({
      ...prev,
      [module]: {
        ...prev[module],
        [permissionType]: !prev[module][permissionType]
      }
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      
      // Convert permissions to API format
      const permissionSet = new Set(); // Use Set to avoid duplicates
      
      Object.keys(permissions).forEach(moduleName => {
        Object.keys(permissions[moduleName]).forEach(action => {
          if (permissions[moduleName][action]) {
            // Find module by name
            const module = modules.find(m => m.name === moduleName);
            if (module) {
              // Handle special cases for Review Queue and Approval Queue
              if (moduleName === 'My Review Queue') {
                if (action === 'Read' || action === 'Edit') {
                  permissionSet.add('licence:review');
                }
              } else if (moduleName === 'My Approval Queue') {
                if (action === 'Read' || action === 'Edit') {
                  permissionSet.add('licence:approve');
                }
              } else if (moduleName === 'Requested Licences') {
                // Requested Licences - handle Edit specially, others use standard CRUD
                if (action === 'Edit') {
                  // Edit will be handled at the end - don't add licence:update here
                } else {
                  // Other actions (Create, Read, Delete) use standard CRUD
                  const actionKey = action.toLowerCase() === 'edit' ? 'update' : action.toLowerCase();
                  permissionSet.add(`${module.permissionKey}:${actionKey}`);
                }
              } else if (module.specialPermission === '*') {
                // Xero Integration - only add if all permissions are enabled (SuperAdmin)
                // For now, just add settings:read if Read is enabled
                if (action === 'Read') {
                  permissionSet.add('settings:read');
                }
              } else {
                // Standard CRUD permissions - use the permissionKey from module
                // Map "Edit" to "update" for API compatibility
                const actionKey = action.toLowerCase() === 'edit' ? 'update' : action.toLowerCase();
                permissionSet.add(`${module.permissionKey}:${actionKey}`);
              }
            }
          }
        });
      });

      // licence:update = Edit for Requested Licences only (Review Queue and Approval Queue use their own permissions)
      permissionSet.delete('licence:update');
      const hasRequestedEdit = permissions['Requested Licences']?.['Edit'] || false;
      if (hasRequestedEdit) {
        permissionSet.add('licence:update');
      }

      // Convert Set to Array to avoid duplicates
      const permissionArray = Array.from(permissionSet);

      await updateRole(id, {
        name: roleName,
        description: roleDescription,
        permissions: permissionArray
      });

      toast.success('Role updated successfully!');
      // Small delay to show toast before navigating
      setTimeout(() => {
        navigate('/settings/roles-permissions');
      }, 500);
    } catch (err) {
      console.error('Failed to save role:', err);
      toast.error(err?.data?.error || err?.message || 'Failed to save role. Please try again.');
    } finally {
      setSaving(false);
    }
  };


  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-gray-500">Loading...</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 w-full max-w-[960px] mx-auto">
      {/* Header */}
      <div className="flex items-center justify-end">
        <button
          onClick={() => navigate('/settings/roles-permissions')}
          className="text-gray-400 hover:text-gray-600 transition-colors"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
            <path d="M18 6L6 18M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
          </svg>
        </button>
      </div>

      <div className="flex items-center">
        <h1 className="text-[28px] font-semibold text-[#0a1f17] leading-[1.4] tracking-[-0.56px]">
          {role ? (roleName || 'View Role') : 'View Role'}
        </h1>
      </div>

      {/* Content Area */}
      <div className="flex flex-col gap-4 pb-4">
        {/* Role Information */}
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1">
            <label className="text-sm text-[#485b54] leading-[1.7]">
              Type
            </label>
            <input
              type="text"
              value={roleName}
              onChange={(e) => setRoleName(e.target.value)}
              placeholder="Name"
              className="bg-white border border-[#ebebeb] rounded-lg px-3 py-2 w-[355px] text-sm text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-500"
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="text-sm text-[#485b54] leading-[1.7]">
              Detail
            </label>
            <textarea
              value={roleDescription}
              onChange={(e) => setRoleDescription(e.target.value)}
              placeholder="Text Area"
              rows={3}
              className="bg-white border border-[#ebebeb] rounded-lg px-3 py-2 w-full min-h-[71px] text-sm text-[#73827c] focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-none"
            />
          </div>
        </div>

        {/* Permissions Table */}
        <div className="bg-white rounded-xl border border-[#f2f2f2] overflow-hidden">
          <div className="flex items-start">
            {/* Module Column */}
            <div className="flex flex-col shrink-0 w-[230px]">
              <div className="bg-[#fafafa] border border-[#f2f2f2] h-[40px] flex items-center px-3 rounded-tl-lg">
                <p className="flex-1 font-medium text-[#485b54] text-base leading-[1.6]">
                  Module
                </p>
              </div>
              {modules.map((module, index) => (
                <div key={module.name} className="flex flex-col">
                  <div className={`border border-[#f2f2f2] h-[40px] flex items-center px-3 ${
                    index === modules.length - 1 ? 'rounded-bl-lg' : ''
                  }`}>
                    <p className="flex-1 font-normal text-[#73827c] text-base leading-[1.6] truncate">
                      {module.name}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Permission Columns */}
            {permissionTypes.map((permissionType, colIndex) => (
              <div key={permissionType} className="flex flex-col flex-1 min-w-0">
                <div className="bg-[#fafafa] border border-[#f2f2f2] h-[40px] flex items-center px-3">
                  <p className="flex-1 font-medium text-[#485b54] text-base leading-[1.6]">
                    {permissionType}
                  </p>
                </div>
                {modules.map((module, rowIndex) => {
                  const isChecked = permissions[module.name]?.[permissionType] || false;
                  const isLastRow = rowIndex === modules.length - 1;
                  const isLastCol = colIndex === permissionTypes.length - 1;
                  
                  // Disable toggles for special permissions - only allow Read and Edit
                  const isDisabled = 
                    (module.specialPermission === 'licence:review' || module.specialPermission === 'licence:approve') && 
                    permissionType !== 'Read' && permissionType !== 'Edit';
                  // Uploads: only Create is used (upload support documents)
                  const isUploadsNonCreate = module.uploadOnlyCreate && permissionType !== 'Create';
                  
                  return (
                    <div key={`${module.name}-${permissionType}`} className="flex flex-col">
                      <div className={`border border-[#f2f2f2] h-[40px] flex items-center justify-center px-3 ${
                        isLastRow && isLastCol ? 'rounded-br-lg' : ''
                      }`}>
                        <Toggle
                          checked={isChecked}
                          onChange={(checked) => handleTogglePermission(module.name, permissionType)}
                          disabled={isDisabled || isUploadsNonCreate}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex items-center justify-end gap-2">
        <button
          onClick={() => navigate('/settings/roles-permissions')}
          className="bg-white border border-[#e7ebe9] text-[#485b54] px-6 py-2 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleSave}
          disabled={saving}
          className="bg-gradient-to-r from-[#40c191] to-[#5ed58d] text-white px-6 py-2 rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
        >
          {saving ? 'Saving...' : 'Update Role'}
        </button>
      </div>
    </div>
  );
};

export default RoleDetails;

